import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package } from "lucide-react";
import { SeriesCard } from "./Scheduled-Order/SeriesCard";
import { mapEtapaToSegment, mapEtapaToGradeName, getSeriesBatchItems } from "@/lib/utils";

interface OrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  quantidade_pedido: number;
  valor_liquido: number;
  valor_total: number;
  delivery_type_id: number;
  delivery_type_number: number;
  brand_id: number;
  is_optional?: boolean;
}

interface ProfessorStepContentProps {
  orderItems: OrderItem[]; // Somente items de professor
  fullOrderItems: OrderItem[];
  updateQuantidade: (index: number, quantidade: number) => void;
  updateQuantidadesBatch: (updates: Array<{ id: number; quantidade: number }>) => void;
  formatCurrency: (value: number) => string;
  isScheduled: boolean;
  isSimulated?: boolean;
  cycleYear?: number;
}

const ProfessorStepContent = ({
  orderItems,
  fullOrderItems,
  updateQuantidade,
  updateQuantidadesBatch,
  formatCurrency,
  isScheduled,
  isSimulated = false,
  cycleYear = 2026,
}: ProfessorStepContentProps) => {
  const [openSeries, setOpenSeries] = useState<Record<string, boolean>>({});

  const toggleSeries = (serie: string) => {
    setOpenSeries(prev => ({
      ...prev,
      [serie]: !prev[serie]
    }));
  };

  // Get segment order for consistent sorting
  const getSegmentOrder = (segmento: string): number => {
    const order: Record<string, number> = {
      "Ed. Infantil": 1,
      "Fund. Anos Iniciais": 2,
      "Fund. Anos Finais": 3,
      "Ensino Médio": 4,
      PV: 5,
    };
    return order[segmento] || 999;
  };

  // Agrupar items de professor por segmento
  const itemsBySegment = useMemo(() => {
    const grouped: Record<string, OrderItem[]> = {};
    orderItems.forEach(item => {
      const segment = mapEtapaToSegment(item.serie);
      if (!grouped[segment]) {
        grouped[segment] = [];
      }
      grouped[segment].push(item);
    });
    return grouped;
  }, [orderItems]);

  // Segmentos ordenados
  const sortedSegments = useMemo(() => {
    return Object.keys(itemsBySegment).sort(
      (a, b) => getSegmentOrder(a) - getSegmentOrder(b)
    );
  }, [itemsBySegment]);

  // Agrupar items por série (grade name) dentro de cada segmento
  const groupItemsBySeries = (items: OrderItem[]) => {
    const grouped: Record<string, OrderItem[]> = {};
    items.forEach((item) => {
      const serie = mapEtapaToGradeName(item.serie);
      if (!grouped[serie]) {
        grouped[serie] = [];
      }
      grouped[serie].push(item);
    });

    // Ordenar items dentro de cada grupo por brand_id
    const getBrandId = (marca: string): number => {
      const brandIdMap: Record<string, number> = {
        "1": 1, "2": 2, "3": 3, "4": 4, "5": 5,
      };
      return brandIdMap[marca] || 0;
    };

    Object.keys(grouped).forEach((serie) => {
      grouped[serie] = grouped[serie].sort((a, b) => {
        const brandIdA = getBrandId(a.marca);
        const brandIdB = getBrandId(b.marca);
        if (brandIdA !== brandIdB) {
          return brandIdA - brandIdB;
        }
        return 0;
      });
    });

    return grouped;
  };

  // Ordena as entradas de série (dentro de um segmento) pela ordem pedagógica real,
  // usando o código numérico bruto de "serie" do primeiro item de cada grupo —
  // Object.entries por si só preserva só a ordem de aparição nos dados, não a ordem certa
  const sortSerieEntries = (grouped: Record<string, OrderItem[]>): [string, OrderItem[]][] => {
    return Object.entries(grouped).sort(([, itemsA], [, itemsB]) => {
      const serieA = parseInt(String(itemsA[0]?.serie ?? ""), 10);
      const serieB = parseInt(String(itemsB[0]?.serie ?? ""), 10);
      if (!isNaN(serieA) && !isNaN(serieB)) {
        return serieA - serieB;
      }
      return 0;
    });
  };

  const handleUpdateQuantity = (
    itemId: number,
    quantity: number,
    updateAllInSeries?: boolean
  ) => {
    if (updateAllInSeries) {
      const targetItem = orderItems.find(item => item.id === itemId);
      if (targetItem) {
        const updates = getSeriesBatchItems(
          orderItems.filter(
            (item) =>
              item.serie === targetItem.serie && item.utilizacao === targetItem.utilizacao
          )
        ).map((item) => ({ id: item.id, quantidade: quantity }));
        updateQuantidadesBatch(updates);
      }
    } else {
      // Encontrar o index global no fullOrderItems
      const globalIndex = fullOrderItems.findIndex(item => item.id === itemId);
      if (globalIndex !== -1) {
        updateQuantidade(globalIndex, quantity);
      }
    }
  };

  // Get segment icon
  const getSegmentIcon = (segment: string) => {
    const icons: Record<string, string> = {
      "Ed. Infantil": "🧒",
      "Fund. Anos Iniciais": "📚",
      "Fund. Anos Finais": "📖",
      "Ensino Médio": "🎓",
      PV: "💼",
    };
    return icons[segment] || "📦";
  };

  return (
    <div className="space-y-6">
      <Card className="border-2 border-blue-300">
        <CardHeader className="bg-blue-50 border-b border-blue-200">
          <CardTitle className="text-lg flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            Pedido
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-6">
            <h4 className="text-md font-semibold mb-3 flex items-center gap-2">
              <span className="text-[35px]">🧑‍🏫</span> Materiais para Professor
            </h4>

            {sortedSegments.map((segment) => {
              const segmentItems = itemsBySegment[segment];
              const groupedItems = groupItemsBySeries(segmentItems);

              return (
                <div key={segment} className="space-y-3">
                  {/* Subtítulo do segmento */}
                  <div className="flex items-center gap-2 pl-2">
                    <span className="text-lg">{getSegmentIcon(segment)}</span>
                    <h5 className="text-sm font-semibold text-gray-700">{segment}</h5>
                    <Badge variant="outline" className="text-xs">
                      {segmentItems.length} {segmentItems.length === 1 ? "item" : "itens"}
                    </Badge>
                  </div>

                  {/* Cards de série */}
                  <div className="space-y-4 pl-4">
                    {sortSerieEntries(groupedItems).map(([serie, serieItems]) => {
                      const uniqueKey = `professor-${segment}-${serie}`;
                      return (
                        <SeriesCard
                          key={uniqueKey}
                          serie={serie}
                          items={serieItems}
                          isOpen={openSeries[uniqueKey] || false}
                          onToggle={() => toggleSeries(uniqueKey)}
                          onUpdateQuantity={handleUpdateQuantity}
                          formatCurrency={formatCurrency}
                          clientHasOrders={true}
                          isScheduledArea={false}
                          isSimulated={isSimulated}
                          cycleYear={cycleYear}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {sortedSegments.length === 0 && (
              <div className="rounded-lg border border-dashed border-muted-foreground/30 p-12 text-center">
                <div className="text-6xl mb-4">🧑‍🏫</div>
                <p className="text-muted-foreground text-lg">
                  Nenhum material para Professor disponível.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfessorStepContent;
